/* Step 3 — Preview: matched base design + adapted floor plan + room-by-room diff. */
import React, { useMemo } from "react";
import DOMPurify from "isomorphic-dompurify";
import { ChevronLeft, ChevronRight, AlertTriangle, Sparkles } from "lucide-react";

export default function StepPreview({ preview, onBack, onContinue }) {
  const { base_design, adaptation, base_floor_plan_svg, floor_plan_svg, price } = preview;

  // Both SVGs come from our own deterministic server renderer, but we
  // sanitise on the client too (defense-in-depth) — DOMPurify with
  // USE_PROFILES.svg keeps geometric/structural tags + standard text
  // styling and strips scripting hooks.
  const safeBaseSvg = useMemo(
    () =>
      DOMPurify.sanitize(base_floor_plan_svg || "", {
        USE_PROFILES: { svg: true, svgFilters: true },
      }),
    [base_floor_plan_svg],
  );
  const safeSvg = useMemo(
    () =>
      DOMPurify.sanitize(floor_plan_svg || "", {
        USE_PROFILES: { svg: true, svgFilters: true },
      }),
    [floor_plan_svg],
  );

  return (
    <section data-testid="studio-step-preview">
      <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">Step 3 of 3</div>
      <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold text-navy">
        Your adapted design
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Closest base design from the Mera Naksha Library, scaled to your plot.
      </p>

      {/* Base 3D + base floor plan + adapted floor plan */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Base design card */}
        <div className="rounded-3xl bg-white border-2 border-border overflow-hidden">
          <div className="aspect-[4/3] bg-sand-alt relative">
            {base_design.image_url && (
              <img
                src={base_design.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${base_design.image_url}` : base_design.image_url}
                alt={base_design.name}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="studio-base-image"
              />
            )}
            <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-navy/90 text-gold text-[10.5px] font-bold uppercase tracking-wider">
              <Sparkles className="h-3 w-3" />
              Base design
            </div>
          </div>
          <div className="p-4">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold">
              ID: <code className="font-mono">{base_design.id}</code>
            </div>
            <div className="mt-1 text-lg font-extrabold text-navy" data-testid="studio-base-name">{base_design.name}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {base_design.plot_width}' × {base_design.plot_depth}' · {base_design.area_sqft} sqft · {base_design.bhk}BHK
            </div>
            <div className="mt-3 text-[11.5px] text-navy leading-snug bg-sand-alt rounded-xl px-3 py-2">
              Room <strong>positions, circulation paths, staircase, and
              kitchen/toilet locations</strong> are preserved exactly from
              this base. Only room <strong>dimensions</strong> are scaled to
              fit your plot.
            </div>
          </div>
        </div>

        {/* Floor plans — base vs adapted (side-by-side proves position preservation) */}
        <div className="lg:col-span-2 rounded-3xl bg-white border-2 border-border p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-bold">
              Floor plan — base vs your plot
            </div>
            <div className="text-[11px] text-muted-foreground tabular-nums">
              scale {adaptation.scale_factor_w}× X / {adaptation.scale_factor_d}× Y
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-center text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">
                Base · {base_design.plot_width}'×{base_design.plot_depth}' · {base_design.area_sqft} sqft
              </div>
              <div
                className="rounded-2xl overflow-hidden bg-sand-alt border border-border/60"
                dangerouslySetInnerHTML={{ __html: safeBaseSvg }}
                data-testid="studio-base-svg"
              />
            </div>
            <div>
              <div className="text-center text-[10.5px] uppercase tracking-wider text-gold-dark font-bold mb-1.5">
                Your plot · adapted · {adaptation.adapted_area_sqft} sqft
              </div>
              <div
                className="rounded-2xl overflow-hidden bg-gold/5 border-2 border-gold/30"
                dangerouslySetInnerHTML={{ __html: safeSvg }}
                data-testid="studio-adapted-svg"
              />
            </div>
          </div>
          <div className="mt-2 text-[11px] text-center text-muted-foreground">
            Same room layout. Only dimensions are scaled.
          </div>
        </div>
      </div>

      {/* Warnings */}
      {adaptation.warnings && adaptation.warnings.length > 0 && (
        <div className="mt-4 rounded-2xl bg-amber-50 border-2 border-amber-200 p-4" data-testid="studio-warnings">
          <div className="flex items-center gap-2 text-amber-800 font-bold text-xs uppercase tracking-wider">
            <AlertTriangle className="h-4 w-4" />
            Please note
          </div>
          <ul className="mt-2 space-y-1.5">
            {adaptation.warnings.map((w) => (
              <li key={w} className="text-sm text-amber-900 leading-snug">• {w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Room-by-room diff */}
      <div className="mt-4 rounded-3xl bg-white border-2 border-border overflow-hidden">
        <div className="px-4 py-3 bg-sand-alt border-b border-border">
          <div className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-bold">
            Room-by-room adaptation
          </div>
          <div className="text-sm text-navy font-bold mt-0.5">
            Base {adaptation.base_area_sqft} sqft → Your {adaptation.adapted_area_sqft} sqft
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold">
              <tr>
                <th className="text-left px-3 py-2">Room</th>
                <th className="text-right px-3 py-2">Original</th>
                <th className="text-right px-3 py-2">Adapted</th>
                <th className="text-right px-3 py-2">Δ Width</th>
                <th className="text-right px-3 py-2">Δ Depth</th>
              </tr>
            </thead>
            <tbody>
              {adaptation.rooms.map((r) => {
                const dw = r.adapted_w - r.original_w;
                const dd = r.adapted_d - r.original_d;
                const fmt = (n) => (n > 0 ? `+${n.toFixed(1)}'` : n < 0 ? `${n.toFixed(1)}'` : "—");
                return (
                  <tr key={r.name} className="border-t border-border/60" data-testid={`studio-room-${r.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    <td className="px-3 py-2 font-bold text-navy">{r.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.original_w}' × {r.original_d}'</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-navy">{r.adapted_w}' × {r.adapted_d}'</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${dw > 0 ? "text-emerald-700" : dw < 0 ? "text-red-700" : "text-muted-foreground"}`}>{fmt(dw)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${dd > 0 ? "text-emerald-700" : dd < 0 ? "text-red-700" : "text-muted-foreground"}`}>{fmt(dd)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-7 rounded-3xl bg-navy text-white p-5 sm:p-6 border-2 border-navy-600 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-bold">Unlock downloads</div>
          <div className="mt-1 text-xl sm:text-2xl font-extrabold">2D Plan (PDF + PNG) + AI 3D Elevation</div>
          <div className="text-[12.5px] text-white/70 mt-1">One-time payment · instant download</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10.5px] uppercase tracking-wider text-white/60 font-bold">Price</div>
            <div className="text-3xl font-extrabold text-gold tabular-nums">₹{price.toLocaleString("en-IN")}</div>
          </div>
          <button
            type="button"
            onClick={onContinue}
            data-testid="studio-continue-payment"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gold text-navy font-extrabold hover:bg-gold-dark hover:text-white"
          >
            Continue
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="mt-5">
        <button
          type="button"
          onClick={onBack}
          data-testid="studio-preview-back"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border-2 border-border text-navy font-bold hover:border-navy/40"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
      </div>
    </section>
  );
}
